import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { AdCheckItem } from '../../campaign-wizard.types';
import { wz } from '../../components/wizard-tokens';
import { WizGradientButton } from '../../components/wizard-ui';
import { GATE_RED_THRESHOLD } from './job-gate.logic';

interface Props {
  open: boolean;
  /** Die nicht erfüllten Punkte des Prüfergebnisses. */
  points: AdCheckItem[];
  /** In den Ergänzungen stehen noch „[…]“-Platzhalter. */
  placeholders?: boolean;
  onAmend: () => void;
  onProceed: () => void;
}

/**
 * Vor dem Weiterschalten: mindestens ein Punkt der Anzeige fehlt – ergänzen oder ohne weitere Optimierung überspringen.
 * „Überspringen“ ist ein deutlicher Button (kein Textlink): Der Nutzer soll nicht glauben, alles müsse grün werden.
 */
const GapDialog: React.FC<Props> = ({ open, points, placeholders = false, onAmend, onProceed }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  return (
    <Dialog
      open={open}
      onClose={onAmend}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <DialogTitle sx={{ fontSize: 20, fontWeight: 700 }}>{t('wizGateTitle')}</DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '8px !important' }}
      >
        {points.length >= GATE_RED_THRESHOLD ? (
          <div style={{ fontSize: 15, color: wz.muted }}>
            {points.length === 1 ? t('wizGateIntroOne') : t('wizGateIntro', { count: points.length })}
          </div>
        ) : null}
        {placeholders ? (
          <div style={{ fontSize: 15, color: wz.text }} data-testid="wiz-gate-placeholders">
            {t('wizGatePlaceholders')}
          </div>
        ) : null}
        {points.length > 0 ? (
        <div
          style={{
            border: `1px solid ${wz.border}`,
            borderRadius: 10,
            padding: '6px 14px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {points.map(p => (
            <div
              key={p.label}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '6px 0',
                fontSize: 14,
              }}
            >
              <RemoveIcon sx={{ fontSize: 18, color: '#ED6C02' }} />
              <div style={{ width: 180, flexShrink: 0, fontWeight: 500, color: wz.textDim }}>
                {p.label}
              </div>
              <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: wz.muted }}>
                {p.comment || t('wizJobCheckMissing')}
              </div>
            </div>
          ))}
        </div>
        ) : null}
        <div style={{ fontSize: 13, color: wz.muted }}>{t('wizGateHint')}</div>
      </DialogContent>
      <DialogActions sx={{ padding: '0 24px 20px', gap: '12px', flexWrap: 'wrap' }}>
        <button type="button" className={classes.skip} onClick={onProceed}>
          {t('wizGateProceed')}
        </button>
        <WizGradientButton onClick={onAmend}>{t('wizGateAmend')}</WizGradientButton>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()({
  skip: {
    height: 40,
    padding: '0 20px',
    boxSizing: 'border-box',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    color: wz.primary,
    background: '#FFF',
    border: `1.5px solid ${wz.primary}`,
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:hover': { background: wz.primarySoft },
  },
});

export default GapDialog;
